import { api } from "./api";

// ════════════════════════════════════════════════════════════════════════════
// Virtual Receptionist API (PRD-RECEPTIONIST.md)
// ════════════════════════════════════════════════════════════════════════════

export interface VrAccount {
  id: string;
  name: string;
  slug: string;
  status: "active" | "setup";
}

export interface VrAppointment {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  status: string;
  confirmationCode: string;
  clientId: string;
  clientName: string;
  clientPhone: string | null;
  clientEmail: string | null;
  serviceName: string | null;
  serviceDurationMinutes: number | null;
  providerName: string | null;
  locationName: string | null;
}

export interface VrAppointmentsResponse {
  timezone: string;
  appointments: VrAppointment[];
}

export function getVrAccounts(): Promise<{ accounts: VrAccount[] }> {
  return api<{ accounts: VrAccount[] }>("/api/vr/accounts");
}

export function getVrAppointments(
  tenantId: string,
  startDate: string,
  endDate: string
): Promise<VrAppointmentsResponse> {
  const params = new URLSearchParams({ startDate, endDate });
  return api<VrAppointmentsResponse>(
    `/api/vr/accounts/${tenantId}/appointments?${params.toString()}`
  );
}

export function isVirtualReceptionist(user: { role: string; isPlatformUser?: boolean } | null): boolean {
  return !!user?.isPlatformUser && user.role === "virtual_receptionist";
}

// ════════════════════════════════════════════════════════════════════════════
// Phase 2 — Queue, outcomes, notes, callbacks
// ════════════════════════════════════════════════════════════════════════════

export type VrConfirmationStatus =
  | "unconfirmed"
  | "confirmed"
  | "no_answer"
  | "voicemail"
  | "reschedule_requested"
  | "bad_contact";

export type VrCallOutcome = Exclude<VrConfirmationStatus, "unconfirmed">;

export interface VrQueueAppointment extends VrAppointment {
  vrConfirmationStatus: VrConfirmationStatus;
  vrConfirmationAttempts: number;
  vrLastAttemptAt: string | null;
  isUrgent: boolean;
  needsEscalation: boolean;
}

export interface VrCallback {
  id: string;
  entityType: "appointment" | "client" | "lead";
  entityId: string | null;
  clientName: string | null;
  phone: string | null;
  dueDate: string;
  dueTime: string | null;
  reason: string | null;
  createdAt: string;
}

export interface VrQueueAccount {
  id: string;
  name: string;
  slug: string;
  timezone: string;
  localHour: number;
  canCallNow: boolean;
  today: string;
  horizonEnd: string;
  appointments: VrQueueAppointment[];
  callbacks: VrCallback[];
  freshLeadCount: number;
}

export interface VrQueueResponse {
  accounts: VrQueueAccount[];
  failedAccounts: number;
}

export interface VrNote {
  id: string;
  entityType: string;
  entityId: string;
  body: string;
  authorId: string;
  authorName: string | null;
  createdAt: string;
}

export function getVrQueue(): Promise<VrQueueResponse> {
  return api<VrQueueResponse>("/api/vr/queue");
}

export function postVrOutcome(
  tenantId: string,
  appointmentId: string,
  outcome: VrCallOutcome,
  note?: string
): Promise<{
  success: boolean;
  vrConfirmationStatus: VrConfirmationStatus;
  vrConfirmationAttempts: number;
  callbackCreated: boolean;
  needsEscalation: boolean;
}> {
  return api(`/api/vr/accounts/${tenantId}/appointments/${appointmentId}/outcome`, {
    method: "POST",
    body: JSON.stringify({ outcome, note: note || undefined }),
  });
}

export function postVrReschedule(
  tenantId: string,
  appointmentId: string,
  date: string,
  startTime: string,
  note?: string
): Promise<{ success: boolean; date: string; startTime: string; endTime: string }> {
  return api(`/api/vr/accounts/${tenantId}/appointments/${appointmentId}/reschedule`, {
    method: "POST",
    body: JSON.stringify({ date, startTime, note: note || undefined }),
  });
}

export function postVrCancel(
  tenantId: string,
  appointmentId: string,
  reason?: string,
  note?: string
): Promise<{ success: boolean }> {
  return api(`/api/vr/accounts/${tenantId}/appointments/${appointmentId}/cancel`, {
    method: "POST",
    body: JSON.stringify({ reason: reason || undefined, note: note || undefined }),
  });
}

export function postVrResendConfirmation(
  tenantId: string,
  appointmentId: string
): Promise<{ success: boolean }> {
  return api(`/api/vr/accounts/${tenantId}/appointments/${appointmentId}/resend-confirmation`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function getVrNotes(
  tenantId: string,
  entityType: "appointment" | "client" | "lead",
  entityId: string
): Promise<{ notes: VrNote[] }> {
  const params = new URLSearchParams({ entityType, entityId });
  return api(`/api/vr/accounts/${tenantId}/notes?${params.toString()}`);
}

export function postVrNote(
  tenantId: string,
  entityType: "appointment" | "client" | "lead",
  entityId: string,
  body: string
): Promise<{ note: VrNote }> {
  return api(`/api/vr/accounts/${tenantId}/notes`, {
    method: "POST",
    body: JSON.stringify({ entityType, entityId, body }),
  });
}

export function postVrCallback(
  tenantId: string,
  data: {
    entityType: "appointment" | "client" | "lead";
    entityId?: string;
    clientName?: string;
    phone?: string;
    dueDate: string;
    dueTime?: string;
    reason?: string;
  }
): Promise<{ callback: VrCallback }> {
  return api(`/api/vr/accounts/${tenantId}/callbacks`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function patchVrCallback(
  tenantId: string,
  callbackId: string,
  status: "done" | "dismissed"
): Promise<{ success: boolean }> {
  return api(`/api/vr/accounts/${tenantId}/callbacks/${callbackId}`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });
}

// ════════════════════════════════════════════════════════════════════════════
// Phase 3 — Facebook leads pipeline
// ════════════════════════════════════════════════════════════════════════════

export type VrLeadStatus = "new" | "contacted" | "callback_scheduled" | "booked" | "not_interested";

export interface VrFbLead {
  id: string;
  metaLeadgenId: string;
  formName: string | null;
  fullName: string | null;
  email: string | null;
  phone: string | null;
  fieldData: { name: string; values: string[] }[];
  status: VrLeadStatus;
  appointmentId: string | null;
  nextFollowupDate: string | null;
  nurtureStage: number;
  attempts: number;
  vrLastAttemptAt: string | null;
  submittedAt: string | null;
  createdAt: string;
}

export interface VrLeadsAccount {
  id: string;
  name: string;
  slug: string;
  timezone: string;
  today: string;
  leads: VrFbLead[];
  counts: Partial<Record<VrLeadStatus, number>>;
}

export function getVrLeads(): Promise<{ accounts: VrLeadsAccount[]; failedAccounts: number }> {
  return api("/api/vr/leads");
}

export function postVrLeadStatus(
  tenantId: string,
  leadId: string,
  status: Exclude<VrLeadStatus, "booked">,
  note?: string
): Promise<{ success: boolean; status: VrLeadStatus; nextFollowupDate: string | null }> {
  return api(`/api/vr/accounts/${tenantId}/leads/${leadId}/status`, {
    method: "POST",
    body: JSON.stringify({ status, note: note || undefined }),
  });
}

export interface VrBookingOptions {
  providers: { id: string; name: string }[];
  services: { id: string; name: string; durationMinutes: number }[];
  consultServiceId: string | null;
}

export function getVrBookingOptions(tenantId: string): Promise<VrBookingOptions> {
  return api(`/api/vr/accounts/${tenantId}/booking-options`);
}

export function postVrLeadBook(
  tenantId: string,
  leadId: string,
  data: { providerId: string; date: string; startTime: string; serviceId?: string; note?: string }
): Promise<{
  success: boolean;
  appointmentId: string;
  confirmationCode: string;
  serviceName: string;
  date: string;
  startTime: string;
  endTime: string;
}> {
  return api(`/api/vr/accounts/${tenantId}/leads/${leadId}/book`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}
